import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Inspection : famille "vérification structurée".
 *
 * Contrôle daté à partir d'une check-list : points de contrôle renseignés,
 * verdict global, souvent photographié ou signé, avec des actions de suite.
 * Construite sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas de contrôle concret ("état des lieux", "contrôle
 * sécurité") : il connaît la famille générique `inspection`. Le vocabulaire
 * métier est porté par le champ `type` (chaîne libre déclarée par l'app), le
 * résultat par `verdict`.
 *
 * Cible (targetRef) : polymorphe, un seul des trois ids est en général
 * renseigné selon le contexte (`custodyId` un bien confié, `assetId` un bien,
 * `placeId` un lieu). Tous optionnels : une inspection peut se rattacher à un
 * ordre de travail ou à un incident plutôt qu'à une cible directe.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma).
 * Les photos et le rapport signé sont des `document` (ids dans `documentIds`) ;
 * les actions de suite sont des `task` (ids dans `followUpTaskIds`).
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible n'est
 * signalé sur cette famille.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille inspection (hors champs standard du socle). */
export const inspectionCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "securite", "etat_des_lieux"). */
  type: z.string().min(1),
  /** Cible : bien confié visé (référence `custody`), optionnel. */
  custodyId: z.string().min(1).optional(),
  /** Cible : bien visé (référence `asset`), optionnel. */
  assetId: z.string().min(1).optional(),
  /** Cible : lieu visé (référence `place`), optionnel. */
  placeId: z.string().min(1).optional(),
  /** Modèle de check-list appliqué (référence `template`), optionnel. */
  templateId: z.string().min(1).optional(),
  /** Intervenant qui a réalisé le contrôle (référence `worker`), optionnel. */
  performedByWorkerId: z.string().min(1).optional(),
  /** Date de réalisation effective du contrôle, optionnel. */
  performedAt: z.date().optional(),
  /** Points de contrôle renseignés (intitulé, résultat, remarque...). Liste structurée, optionnel. */
  checkpoints: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Verdict global, chaîne libre pilotée par l'app (ex. "conforme", "non_conforme", "reserve"). Optionnel. */
  verdict: z.string().min(1).optional(),
  /** Actions de suite déclenchées par le contrôle (références `task`), optionnel. */
  followUpTaskIds: z.array(z.string()).optional(),
  /** Ordre de travail lié (référence `work-order`), optionnel. */
  workOrderId: z.string().min(1).optional(),
  /** Incident lié (référence `incident`), optionnel. */
  incidentId: z.string().min(1).optional(),
  /** Pièces jointes : photos et rapport signé (références `document`), optionnel. */
  documentIds: z.array(z.string()).optional(),
} as const;

/** Schéma complet de la famille inspection (champs standard + champs métier). */
export const InspectionSchema = z.object({
  ...baseEntityFields,
  ...inspectionCoreFields,
});

/** Type d'une inspection. */
export type Inspection = z.infer<typeof InspectionSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const InspectionCreateInputSchema = z.object({
  ...inspectionCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une inspection. */
export type InspectionCreateInput = z.infer<typeof InspectionCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const InspectionUpdateInputSchema = z
  .object({ ...inspectionCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'une inspection. */
export type InspectionUpdateInput = z.infer<typeof InspectionUpdateInputSchema>;
